import React, { useEffect, useState, useMemo } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Funds & Settlement tab — cross-deal control room for the money-motion phase.
 * All final actions (record funds received, record clearance, record settlement,
 * correction) happen inside the Deal Room where the domain widgets already
 * exist. This tab surfaces the queue and flags what's blocking each deal.
 */
const FUNDING_STATUSES = [
  "funding_pending",
  "funding_confirmed",
  "transfer_pending",
  "buyer_review",
  "ready_for_settlement",
  "disputed",
  "settled",
];

export default function FundsSettlementTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [phase, setPhase] = useState("live");   // "live" | "settled" | "all"
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/deals-detail", { params: { limit: 500, q: q || undefined } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [q]);

  const filtered = useMemo(() => {
    if (phase === "all") return items;
    if (phase === "settled") return items.filter((d) => d.settlement_recorded);
    return items.filter((d) => FUNDING_STATUSES.includes(d.status) && !d.settlement_recorded);
  }, [items, phase]);

  const bucketCounts = useMemo(() => {
    const b = {
      funds_sent_not_received: 0,
      funds_received_not_cleared: 0,
      cleared_docs_pending: 0,
      awaiting_buyer_accept: 0,
      ready: 0,
      disputed: 0,
    };
    for (const d of items) {
      if (d.settlement_recorded) continue;
      if (d.dispute_status === "open") { b.disputed++; continue; }
      if (d.funds_sent && !d.funds_received) b.funds_sent_not_received++;
      else if (d.funds_received && !d.funds_cleared) b.funds_received_not_cleared++;
      else if (d.funds_cleared && !d.transfer_package_complete) b.cleared_docs_pending++;
      else if (d.transfer_package_complete && !d.buyer_accepted) b.awaiting_buyer_accept++;
      else if (d.settlement_ready) b.ready++;
    }
    return b;
  }, [items]);

  return (
    <div className="space-y-4" data-testid="admin-funds-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 border-l border-t border-border">
        <Kpi label="Buyer Funds Sent" value={bucketCounts.funds_sent_not_received} testid="funds-kpi-sent" />
        <Kpi label="Funds Received" value={bucketCounts.funds_received_not_cleared} testid="funds-kpi-received" />
        <Kpi label="Cleared · Docs Pending" value={bucketCounts.cleared_docs_pending} testid="funds-kpi-cleared" />
        <Kpi label="Awaiting Buyer Accept" value={bucketCounts.awaiting_buyer_accept} testid="funds-kpi-accept" />
        <Kpi label="Ready for Settlement" value={bucketCounts.ready} tone="ok" testid="funds-kpi-ready" />
        <Kpi label="Open Disputes" value={bucketCounts.disputed} tone={bucketCounts.disputed > 0 ? "alert" : ""} testid="funds-kpi-disputed" />
      </div>

      <div className="flex flex-wrap items-center gap-2 border border-border p-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: deal id, buyer, seller email…" className="rounded-none max-w-md" data-testid="funds-search" />
        <Select value={phase} onValueChange={setPhase}>
          <SelectTrigger className="rounded-none w-56" data-testid="funds-phase-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="live">Live (funding + settlement)</SelectItem>
            <SelectItem value="settled">Settled only</SelectItem>
            <SelectItem value="all">All deals</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-none uppercase text-xs h-9 ml-auto" onClick={load} data-testid="funds-refresh">Refresh</Button>
      </div>

      <div className="border border-border overflow-x-auto" data-testid="funds-table">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-muted/50 text-left">
            <tr>
              <Th>Deal</Th><Th>Status</Th><Th>Amount</Th><Th>Sent</Th><Th>Received</Th><Th>Cleared</Th>
              <Th>Docs Pkg</Th><Th>Buyer Accept</Th><Th>Dispute</Th><Th>Gate</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">Nothing in this phase.</td></tr>}
            {filtered.map((d) => <Row key={d.deal_id} d={d} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-muted-foreground text-[10px] whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
const Kpi = ({ label, value, tone, testid }) => {
  const t = tone === "ok" ? "text-emerald-600" : tone === "alert" ? "text-red-600" : "";
  return (
    <div className="p-4 border-r border-b border-border" data-testid={testid}>
      <div className="font-overline text-[10px] text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 font-light ${t}`}>{value ?? 0}</div>
    </div>
  );
};
const Yes = () => <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" title="Yes" />;
const NoDot = () => <span className="inline-block w-2 h-2 rounded-full bg-zinc-400" title="No" />;

function Row({ d }) {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);

  const loadPreview = async () => {
    setOpen((v) => !v);
    if (!preview) {
      try {
        const r = await api.get(`/deals/${d.deal_id}/settlement/preview`);
        setPreview(r.data);
      } catch { setPreview({ error: "Preview unavailable" }); }
    }
  };

  return (
    <>
      <tr className="border-t border-border align-top" data-testid={`funds-row-${d.deal_id}`}>
        <Td className="font-mono text-[11px]">
          <Link to={`/deals/${d.deal_id}`} className="text-primary hover:underline">{d.deal_id.slice(-10)}</Link>
          <div className="text-[10px] text-muted-foreground">{d.buyer?.email || d.buyer?.id?.slice(-6)}</div>
        </Td>
        <Td><span className="text-[10px] font-mono uppercase">{d.status?.replace(/_/g, " ")}</span></Td>
        <Td className="font-mono-data text-[12px]">{fmtMoney(d.final_amount)}</Td>
        <Td className="text-center">{d.funds_sent ? <Yes /> : <NoDot />}</Td>
        <Td className="text-center">{d.funds_received ? <Yes /> : <NoDot />}</Td>
        <Td className="text-center">{d.funds_cleared ? <Yes /> : <NoDot />}</Td>
        <Td className="text-[10px] font-mono">
          {d.transfer_docs_count}{" "}
          {d.transfer_package_complete ? <Yes /> : <NoDot />}
        </Td>
        <Td className="text-center">{d.buyer_accepted ? <Yes /> : <NoDot />}</Td>
        <Td>
          {d.dispute_status === "open" ? <span className="text-[10px] font-mono uppercase text-red-600">Open</span> : <span className="text-[10px] font-mono text-muted-foreground">—</span>}
        </Td>
        <Td>
          {d.settlement_recorded ? (
            <span className="text-[10px] font-mono uppercase text-emerald-600">Recorded</span>
          ) : d.settlement_ready ? (
            <span className="text-[10px] font-mono uppercase text-emerald-600">Ready</span>
          ) : (
            <span className="text-[10px] font-mono text-muted-foreground" title={d.settlement_gate_unmet?.join(", ")}>
              Blocked ({d.settlement_gate_unmet?.length || 0})
            </span>
          )}
        </Td>
        <Td>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="rounded-none text-[10px] h-7 uppercase" onClick={loadPreview} data-testid={`funds-preview-${d.deal_id}`}>
              {open ? "Hide" : "Preview"}
            </Button>
            <Link to={`/deals/${d.deal_id}`}>
              <Button size="sm" className="rounded-none text-[10px] h-7 uppercase" data-testid={`funds-open-${d.deal_id}`}>Deal Room</Button>
            </Link>
          </div>
        </Td>
      </tr>
      {open && (
        <tr className="bg-muted/30 border-t border-border" data-testid={`funds-preview-row-${d.deal_id}`}>
          <td colSpan={11} className="p-4">
            {!preview ? (
              <div className="text-muted-foreground text-sm">Loading fee preview…</div>
            ) : preview.error ? (
              <div className="text-muted-foreground text-sm">{preview.error}</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <FeeCell label="Sale Price" value={fmtMoney(d.final_amount)} />
                <FeeCell label="Buyer Fee" value={fmtMoney(preview.fees?.buyer_fee || 0)} />
                <FeeCell label="Seller Fee" value={fmtMoney(preview.fees?.seller_fee || 0)} />
                <FeeCell label="Platform Total" value={fmtMoney(preview.fees?.platform_total || preview.fees?.total || 0)} />
                <FeeCell label="Net Seller Proceeds" value={fmtMoney(preview.net_seller_proceeds || 0)} tone="ok" />
                {preview.settlement_gate?.unmet_conditions?.length > 0 && (
                  <div className="col-span-full">
                    <div className="font-overline text-red-600 text-[10px]">Settlement Blocked By:</div>
                    <div className="text-[11px] font-mono">
                      {preview.settlement_gate.unmet_conditions.join(" · ")}
                    </div>
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const FeeCell = ({ label, value, tone }) => (
  <div>
    <div className="font-overline text-[10px] text-muted-foreground">{label}</div>
    <div className={`font-mono-data text-lg ${tone === "ok" ? "text-emerald-600" : ""}`}>{value}</div>
  </div>
);
